import licenseManagerSelectors from 'store/licenseManager/selectors'
import removeRedactedContentfulArray from 'utils/removeRedactedContentfulArray'
import { getLicenseFromSchoolOptionText } from 'utils/productHelpers'
import {
  getCurrentProductFromUrl,
  getCurrentProductFilter,
  getFilterTypeFromPathname,
  getInitialSelectedSchool,
} from './dataUtils'
import {
  INITIAL_COURSE,
  MODAL_CONTENT_TYPES,
  MODAL_FLOW_TYPES,
} from './constants'

// Helper function to create state updater that handles side effects
export const createStateUpdater = (setState, sideEffectsRef) => {
  return (newState, sideEffects = {}) => {
    setState(newState)
    sideEffectsRef.current = sideEffects
  }
}

export const handleResetFocus = props => {
  props.setFocus && props.setFocus()
}

export const resetState = (state, props) => {
  const currentProductFilter = getCurrentProductFilter(props.pathName)

  const newState = {
    isFormValid: false,
    inputErrors: {
      className: {
        errors: null,
      },
      selectedGrade: '',
    },
    courseInstanceName: props.courseInstanceName || '',
    licensesWithGrades: [],
    modalContent: null,
    modalFlow: null,
    selectedCourse: INITIAL_COURSE,
    selectedSchool: getInitialSelectedSchool(
      props.currentSite,
      currentProductFilter,
      props.allSites,
    ),
    submitAttempts: 0,
  }

  return {
    newState,
    sideEffects: {
      handleResetFocus: true,
    },
  }
}

export const clickCreateSideEffect = (licenses, handleLicenseChange) => {
  const hasNoLicenses = !Array.isArray(licenses) || licenses.length === 0
  const hasMoreThanOneLicense = Array.isArray(licenses) && licenses.length > 1

  if (hasNoLicenses || hasMoreThanOneLicense) {
    return
  }

  // If only one license, auto-select that license
  const e = { value: licenses[0].siteName }
  handleLicenseChange(e, licenses)
}

export const createHandleCreate = filterConfig => (state, props) => {
  const {
    activeDigitalLicenses,
    contentfulCourses,
    userAccess,
    userContext,
  } = props
  const { selectedCourse, selectedSchool } = state || {}

  const availableContentfulCourses =
    removeRedactedContentfulArray(contentfulCourses) || []

  let updatedSelectedCourse = { ...selectedCourse }
  let updatedSelectedGrade = state.selectedGrade || ''

  // Use userContext to find the right site and program for auto-selection
  if (userContext && selectedSchool) {
    const { siteId } = selectedSchool
    const { sites } = userContext

    // Find the selected site in userContext
    const selectedSite = sites?.find(site => site.siteId === siteId)

    if (selectedSite && selectedSite.programs) {
      // Get the current product from URL to determine the program key
      const currentProduct = getCurrentProductFromUrl(props.pathName)
      let programKey = currentProduct?.lmsKeyV2 || 'k5SelDigital' // default fallback

      // TODO: hack to fix lmskey's uppercase first letter. Unknown why this is happening.
      if (programKey === 'MspDigital') {
        programKey = 'mspDigital'
      }

      if (programKey === 'K5SelDigital') {
        programKey = 'k5SelDigital'
      }

      // Get the program from the selected site
      const program = selectedSite.programs[programKey]

      if (program && program.courses) {
        // Use the filterConfig's getRelevantCourseList function to filter courses
        const relevantCourses = filterConfig.getRelevantCourseList
          ? filterConfig.getRelevantCourseList(program.courses)
          : program.courses.filter(course => course.name)

        // Auto-select if there's only one relevant course available
        if (relevantCourses.length === 1) {
          const course = relevantCourses[0]
          updatedSelectedCourse.id = course.name
          updatedSelectedCourse.name = course.name

          // Convert curriculum type to display name for small groups
          if (filterConfig.convertGradeToDisplayValue) {
            updatedSelectedGrade =
              filterConfig.convertGradeToDisplayValue(course.name) ||
              course.name
          }
        }
      }
    }
  }

  const licensesWithGrades = licenseManagerSelectors.getLicensesWithGrades(
    activeDigitalLicenses,
    userAccess,
    getFilterTypeFromPathname(props.pathName),
  )

  // Reorder grades using contentful order
  licensesWithGrades.forEach(licenseWithGrade => {
    const originalGrades = licenseWithGrade.grades
    const sortedGrades = []

    availableContentfulCourses.forEach(contentfulCourse => {
      const index = originalGrades.findIndex(grade =>
        contentfulCourse.displayTitle
          .toLowerCase()
          .includes(grade.toLowerCase()),
      )

      if (index !== -1) {
        originalGrades.splice(index, 1)
        sortedGrades.push(contentfulCourse.displayTitle)
      }
    })

    // Concat any original grade remaining (this should not happen)
    const allGrades = sortedGrades.concat(originalGrades)

    // Filter grades using the configured filter function
    const filteredGrades = filterConfig.filterFunction(allGrades)
    const sortedFilteredGrades = filterConfig.sortFunction(filteredGrades)

    // Convert grades to display names for the UI
    const displayGrades = sortedFilteredGrades.map(grade => {
      // If it's a curriculum type, convert to display name
      if (filterConfig.convertGradeToDisplayValue) {
        return filterConfig.convertGradeToDisplayValue(grade) || grade
      }
      return grade
    })

    licenseWithGrade.grades = displayGrades
  })

  // If selectedSchool is prefilled but doesn't have grades, populate them from licensesWithGrades
  let updatedSelectedSchool = selectedSchool
  if (
    selectedSchool &&
    selectedSchool.id &&
    (!selectedSchool.grades || selectedSchool.grades.length === 0)
  ) {
    const matchingLicense = licensesWithGrades.find(
      license => license.licenseId === selectedSchool.id,
    )
    if (matchingLicense) {
      updatedSelectedSchool = {
        ...selectedSchool,
        grades: matchingLicense.grades,
      }
    }
  }

  const newState = {
    ...state,
    licensesWithGrades,
    modalContent: MODAL_CONTENT_TYPES.Create,
    modalFlow: MODAL_FLOW_TYPES.Create,
    previous: state.modalContent,
    selectedCourse: updatedSelectedCourse,
    selectedGrade: updatedSelectedGrade,
    selectedSchool: updatedSelectedSchool,
  }

  // Only trigger side effects if we didn't auto-select a course/grade
  // This prevents side effects from clearing the auto-selected values
  const hasAutoSelectedCourse =
    updatedSelectedCourse.id && updatedSelectedCourse.id !== selectedCourse?.id
  const hasAutoSelectedGrade =
    updatedSelectedGrade && updatedSelectedGrade !== state.selectedGrade

  return {
    newState,
    sideEffects: {
      // Only trigger clickCreateSideEffect if we didn't auto-select anything
      clickCreateSideEffect: !hasAutoSelectedCourse && !hasAutoSelectedGrade,
    },
  }
}

export const handleError = (passedError, state) => {
  const { modalContent: previous, submitAttempts = 0 } = state || {}
  const newState =
    submitAttempts < 2
      ? {
          ...state,
          modalContent: MODAL_CONTENT_TYPES.Error,
          submitAttempts: submitAttempts + 1,
          previous,
        }
      : {
          ...state,
          modalContent: MODAL_CONTENT_TYPES.ErrorDetails,
          error: passedError.message,
          submitAttempts,
          previous,
        }

  return { newState }
}

export const createHandleSubmit = (
  programConfig,
  errorMessage,
  errorLogMessage,
) => async (state, props) => {
  const { createCourseInstanceV2ProgramFlex, userContext } = props || {}
  const { courseInstanceName, modalContent, selectedGrade, selectedSchool } =
    state || {}

  try {
    // The createCourseInstanceV2ProgramFlex operation only needs userContext and classTitle
    const classTitle = courseInstanceName.trim()

    // Filter the userContext to include only the selected site, program, and course
    const { siteId } = selectedSchool || {}
    const { sites } = userContext || {}

    // Find the selected site
    const selectedSite = sites?.find(site => site.siteId === siteId) || {}

    const selectedGradeLabel = programConfig.convertGradeNameToLabel(
      selectedGrade,
    )

    // Determine the program name using the configured program mapping logic
    const programName = programConfig.getProgramName(selectedGradeLabel)

    // Get the program from the selected site
    const program = selectedSite.programs?.[programName] || {}

    // Find the course that matches the selected grade/curriculum type
    const programHasGrade = program.courses?.find(
      course => course.name === selectedGradeLabel,
    )

    let course = {}
    if (programHasGrade) {
      course = { name: selectedGradeLabel, version: null }
    } else {
      throw new Error('Program does not have the selected grade')
    }

    // Create a filtered site with only the selected program and course
    const filteredSite = {
      siteId: selectedSite.siteId,
      claims: selectedSite.claims,
      programs: {
        [programName]: {
          ...(program.grades ? { grades: program.grades } : {}),
          ...(program.courses ? { courses: [course] } : {}),
          preferences: program.preferences || {},
        },
      },
    }

    // Create the filtered userContext
    const filteredUserContext = {
      sites: [filteredSite],
    }

    // Call the new operation with the filtered userContext and classTitle
    // The operation returns the response data directly and also updates Redux with fresh data
    const responseData = await createCourseInstanceV2ProgramFlex(
      filteredUserContext,
      classTitle,
    )

    // Get the programLmsKey to find the newly created course
    const programLmsKey = getCurrentProductFromUrl(props.pathName)?.lmsKeyV2

    // We can either use the direct response or get the data from Redux
    // Option 1: Use the direct response data
    let newCourse = null

    if (responseData) {
      // TODO: LEARN-23339 discuss with team if this is the best way to handle this or if it should be an array
      // Newly created course does NOT come back as an array (as it does form instructor/me GET calls). It comes back as a single object.
      newCourse = responseData?.[programLmsKey]?.siteEducator
    } else {
      throw new Error(errorMessage, JSON.stringify(responseData))
    }

    // TODO: LEARN-23339 NEED TO FIGURE OUT HOW TO FAIL if newCourse does not have appropriate fields. This set state is flakey

    const successState = {
      ...state,
      selectedCourse: {
        id: newCourse?.registration_id,
        name: newCourse?.class_title,
      },
      modalContent: MODAL_CONTENT_TYPES.Success,
      courseInstanceName: newCourse?.class_title,
      previous: modalContent,
    }

    return {
      newState: successState,
      sideEffects: {
        deletingCourseCreatedStatus: true,
      },
    }
  } catch (err) {
    console.error(errorLogMessage, err)
    return handleError(err, state)
  }
}

export const goBackOneModal = state => {
  const newState = {
    ...state,
    modalContent: state.previous,
    previous: state.modalContent,
  }

  return { newState }
}

export const handleClassNameChange = (e, state) => {
  const { target } = e || {}
  const { name, value } = target || {}
  const newState = { ...state, [name]: value }

  return { newState, sideEffects: { validateForm: true } }
}

export const handleGradeChange = (e, courses = [], state) => {
  const { option, target } = e || {}
  const { name } = target || {}
  const matchedCourse = courses.find(course =>
    course.displayTitle?.includes(option),
  )
  const { id: matchedCourseId } = matchedCourse || {}
  const noCourse = { selectedCourse: INITIAL_COURSE }

  // Save the display name directly to selectedGrade
  const selectedGradeValue = e.target.value

  const newState = matchedCourse
    ? {
        ...state,
        [name]: { name: option, id: matchedCourseId },
        selectedGrade: selectedGradeValue,
      }
    : { ...state, ...noCourse, selectedGrade: selectedGradeValue }

  return { newState, sideEffects: { validateForm: true } }
}

const findSiteWithLicense = (allSites, matchedLicense) => {
  return allSites.find(site =>
    site.licenses?.some(
      license => license.id.toString() === matchedLicense.licenseId,
    ),
  )
}

const createSelectedSchool = matchedLicense => {
  return matchedLicense ? { ...matchedLicense } : {}
}

export const createHandleLicenseChange = () => (
  e,
  licenses = [],
  state,
  props,
) => {
  const { value: selectedLicense } = e || {}
  let matchedLicense =
    typeof selectedLicense === 'string' && Array.isArray(licenses)
      ? getLicenseFromSchoolOptionText(selectedLicense, licenses)
      : null

  // Enrich matchedLicense with siteId by finding which site contains this license
  if (matchedLicense && props.allSites) {
    const siteWithLicense = findSiteWithLicense(props.allSites, matchedLicense)
    if (siteWithLicense) {
      matchedLicense = {
        ...matchedLicense,
        siteId: siteWithLicense.id,
      }
    }
  }

  const newState = {
    ...state,
    selectedSchool: createSelectedSchool(matchedLicense),
  }

  return {
    newState,
    sideEffects: {
      validateForm: true,
    },
  }
}

const performUpdate = async (
  updateCourseInstance,
  courseInstanceName,
  props,
) => {
  const { course, fetchInstructorData, userContext } = props
  const { instance: courseInstance } = course || {}

  const courseData = { ...course }
  courseData.title = courseInstanceName
  courseData.instanceId = courseInstance

  const updatedCourse = await updateCourseInstance(courseData)
  const { title: updatedCourseTitle } = updatedCourse || {}

  await fetchInstructorData(userContext)

  return { updatedTitle: updatedCourseTitle }
}
export const createHandleSaveEdit = updateErrorLogMessage => async (
  state,
  props,
) => {
  const { updateCourseInstance } = props || {}
  const { courseInstanceName } = state || {}

  try {
    const result = await performUpdate(
      updateCourseInstance,
      courseInstanceName,
      props,
    )

    const successState = {
      ...state,
      modalContent: MODAL_CONTENT_TYPES.Success,
      courseInstanceName: result.updatedTitle,
      previous: state.modalContent,
    }

    return {
      newState: successState,
      sideEffects: {
        deletingCourseCreatedStatus: true,
      },
    }
  } catch (err) {
    console.error(updateErrorLogMessage, err)
    return handleError(err, state)
  }
}

export const createHandleViewClass = viewClassConfig => (state, props) => {
  return viewClassConfig.createViewClassBehavior(state, props)
}

export const createHandleCancel = () => (state, props) => {
  const newState = {
    ...state,
    courseInstanceName: props.courseInstanceName || '',
    inputErrors: {
      className: { errors: null },
      selectedGrade: '',
    },
    isFormValid: false,
    modalContent: null,
    modalFlow: null,
    previous: null,
  }

  return {
    newState,
    sideEffects: {
      handleResetFocus: true,
    },
  }
}

export const handleDeleteSubmit = state => {
  const newState = {
    ...state,
    modalContent: MODAL_CONTENT_TYPES.Delete,
    modalFlow: MODAL_FLOW_TYPES.Delete,
  }

  return { newState }
}

export const handleEditSubmit = state => {
  const newState = {
    ...state,
    modalContent: MODAL_CONTENT_TYPES.Edit,
    modalFlow: MODAL_FLOW_TYPES.Edit,
  }

  return { newState }
}

export const createHandleDelete = deleteErrorLogMessage => async (
  state,
  props,
) => {
  const { deleteCourseInstance, course, fetchInstructorData, userContext } =
    props || {}
  const { modalContent } = state || {}
  const { instance: courseInstance } = course || {}

  try {
    await deleteCourseInstance(courseInstance)
    await fetchInstructorData(userContext)

    const newState = {
      ...state,
      courseInstanceName: null,
      modalContent: null,
      previous: modalContent,
    }

    return { newState }
  } catch (err) {
    console.error(deleteErrorLogMessage, err)
    return handleError(err, state)
  }
}

export const createValidateForm = checkBlockingValidations => async (
  state,
  props,
) => {
  const validationResult = await checkBlockingValidations(state, props)

  return { newState: { ...state, ...validationResult } }
}
